'use client';

import { useState, useEffect, use } from 'react';
import { useRouter } from 'next/navigation';
import { getCharacterById, updateCharacter, deleteCharacter, askQwen } from '@/lib/actions';
import { User, ArrowLeft, Sparkles, Save, Trash2, Wand2, Plus, X } from 'lucide-react';
import Link from 'next/link';

export default function EditCharacterPage({ params }: { params: Promise<{ id: string }> }) {
  const { id } = use(params);
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [brainDump, setBrainDump] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    species: '',
    faction: '',
    role: '',
    background: '',
    personality: '',
    abilities: '',
    custom_fields: '[]'
  });

  const [customFields, setCustomFields] = useState<{ label: string; value: string }[]>([]);

  useEffect(() => {
    async function load() {
      const char = await getCharacterById(id);
      if (char) {
        setFormData({
          name: char.name || '',
          species: char.species || '',
          faction: char.faction || '',
          role: char.role || '',
          background: char.background || '',
          personality: char.personality || '',
          abilities: char.abilities || '',
          custom_fields: char.custom_fields || '[]'
        });
        try {
          setCustomFields(JSON.parse(char.custom_fields || '[]'));
        } catch (e) {
          setCustomFields([]);
        }
      }
    }
    load();
  }, [id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCustomChange = (index: number, field: 'label' | 'value', val: string) => {
    const newFields = [...customFields];
    newFields[index][field] = val;
    setCustomFields(newFields);
  };

  const addCustomField = () => {
    setCustomFields([...customFields, { label: '', value: '' }]);
  };

  const removeCustomField = (index: number) => {
    setCustomFields(customFields.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const data = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (key !== 'custom_fields') data.append(key, value);
    });
    data.append('custom_fields', JSON.stringify(customFields));

    try {
      await updateCharacter(id, data);
      router.push(`/characters/${id}`);
      router.refresh();
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm("Are you sure you want to delete this character? This cannot be undone.")) return;
    setIsDeleting(true);
    try {
      await deleteCharacter(id);
      router.push('/');
      router.refresh();
    } catch (err) {
      console.error(err);
      setIsDeleting(false);
    }
  };

  const handleBrainDump = async () => {
    if (!brainDump.trim()) return;
    setIsParsing(true);
    try {
      const prompt = `
        Update this character based on these new notes: "${brainDump}"
        Current data: ${JSON.stringify(formData)}
        Return ONLY a complete JSON object with all fields.
      `;
      const response = await askQwen(prompt, "You are a helpful assistant that updates character JSON.");
      const jsonStr = response.match(/\{[\s\S]*\}/)?.[0];
      if (jsonStr) {
        const parsed = JSON.parse(jsonStr);
        setFormData(prev => ({ ...prev, ...parsed }));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsParsing(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
      <header className="p-6 border-b border-slate-800 bg-slate-900/50 flex items-center justify-between">
        <Link href={`/characters/${id}`} className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
          <ArrowLeft size={20} /> Cancel
        </Link>
        <h1 className="text-xl font-serif font-bold italic text-purple-400">Editing {formData.name}</h1>
        <button onClick={handleDelete} disabled={isDeleting} className="text-red-400 hover:text-red-300 flex items-center gap-2 text-sm font-bold bg-red-950/30 px-4 py-2 rounded-lg border border-red-900/50">
           <Trash2 size={16} /> {isDeleting ? 'Deleting...' : 'Delete'}
        </button>
      </header>

      <main className="flex-1 max-w-6xl mx-auto w-full p-8 grid grid-cols-1 lg:grid-cols-2 gap-12">
        <section className="space-y-6">
          <div className="flex items-center gap-3 text-purple-400">
             <Sparkles size={24} />
             <h2 className="text-2xl font-serif font-bold">Refine with AI</h2>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4">
             <textarea 
               value={brainDump}
               onChange={(e) => setBrainDump(e.target.value)}
               placeholder="Add new details like 'She just found an ancient sword' or 'She actually hates being the leader'..."
               className="w-full h-32 bg-slate-950 border border-slate-800 rounded-xl p-4 text-sm"
             />
             <button onClick={handleBrainDump} disabled={isParsing} className="w-full bg-purple-600 hover:bg-purple-500 py-3 rounded-xl flex items-center justify-center gap-2">
               {isParsing ? 'Oracle is thinking...' : <><Wand2 size={18} /> Update Form with AI</>}
             </button>
          </div>

          <div className="pt-8">
            <div className="flex items-center justify-between mb-4">
               <h3 className="text-xl font-serif font-bold italic text-slate-300">Custom Categories</h3>
               <button onClick={addCustomField} className="flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300 font-bold">
                 <Plus size={14} /> Add Category
               </button>
            </div>
            <div className="space-y-4">
               {customFields.map((field, i) => (
                 <div key={i} className="flex gap-2 items-start bg-slate-900/50 p-4 rounded-xl border border-slate-800">
                    <div className="flex-1 space-y-2">
                       <input 
                         placeholder="Category Label (e.g. Weapon)" 
                         value={field.label}
                         onChange={(e) => handleCustomChange(i, 'label', e.target.value)}
                         className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-1 text-xs font-bold text-purple-300 uppercase tracking-widest"
                       />
                       <textarea 
                         placeholder="Details..."
                         value={field.value}
                         onChange={(e) => handleCustomChange(i, 'value', e.target.value)}
                         className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm"
                       />
                    </div>
                    <button onClick={() => removeCustomField(i)} className="p-1 text-slate-600 hover:text-red-400 transition-colors">
                       <X size={16} />
                    </button>
                 </div>
               ))}
            </div>
          </div>
        </section>

        <form onSubmit={handleSubmit} className="space-y-6 bg-slate-900/50 border border-slate-800 p-8 rounded-3xl">
           <div className="space-y-4">
              <label className="block">
                <span className="text-slate-500 text-xs font-mono uppercase tracking-widest ml-1">Name</span>
                <input required name="name" value={formData.name} onChange={handleChange} className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3" />
              </label>
              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-slate-500 text-xs font-mono uppercase tracking-widest ml-1">Species</span>
                  <input name="species" value={formData.species} onChange={handleChange} className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3" />
                </label>
                <label className="block">
                  <span className="text-slate-500 text-xs font-mono uppercase tracking-widest ml-1">Faction</span>
                  <input name="faction" value={formData.faction} onChange={handleChange} className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3" />
                </label>
              </div>
              <label className="block">
                <span className="text-slate-500 text-xs font-mono uppercase tracking-widest ml-1">Role</span>
                <input name="role" value={formData.role} onChange={handleChange} className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3" />
              </label>
              <label className="block">
                <span className="text-slate-500 text-xs font-mono uppercase tracking-widest ml-1">Background</span>
                <textarea name="background" value={formData.background} onChange={handleChange} rows={4} className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-4 resize-none" />
              </label>
              <label className="block">
                <span className="text-slate-500 text-xs font-mono uppercase tracking-widest ml-1">Personality</span>
                <textarea name="personality" value={formData.personality} onChange={handleChange} rows={3} className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-4 resize-none" />
              </label>
              <label className="block">
                <span className="text-slate-500 text-xs font-mono uppercase tracking-widest ml-1">Abilities</span>
                <textarea name="abilities" value={formData.abilities} onChange={handleChange} rows={3} className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-4 resize-none" />
              </label>
           </div>
           <button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-500 py-4 rounded-xl font-bold flex items-center justify-center gap-2">
              <Save size={20} /> {loading ? 'Saving...' : 'Save Changes'}
           </button>
        </form>
      </main>
    </div>
  );
}
